<template>
  <section class="i-roi-section">
    <div class="container">
      <div class="label mb-3">The math</div>
      <h2 class="headline">Inaction has a cost.<br />Here's what it is.</h2>
      <p
        class="body-lg"
        style="color: var(--text-body); margin-top: 12px; max-width: 540px"
      >
        The median salary increase for clients who act on their session is $25,400. The session is ${{ pricing }}. This
        is not an argument about value. It's arithmetic.
      </p>

      <div class="roi-visual">
        <div class="roi-box">
          <div class="roi-box__label">Session cost</div>
          <div class="roi-box__amount roi-box__amount--small">${{ pricing }}</div>
          <div class="roi-box__sub">one time, no subscription</div>
        </div>
        <div class="roi-arrow">→</div>
        <div class="roi-multiplier">
          <div class="roi-multiplier__label">Median return</div>
          <div class="roi-multiplier__amount">519x</div>
          <div class="roi-multiplier__sub">average ROI on session cost</div>
        </div>
        <div class="roi-arrow">→</div>
        <div class="roi-box">
          <div class="roi-box__label">Median outcome</div>
          <div class="roi-box__amount roi-box__amount--large">$25,400</div>
          <div class="roi-box__sub">salary increase (annual)</div>
        </div>
      </div>

      <div class="roi-callout">
        Every month you spend in your current role without a plan costs you roughly
        <strong>$2,117 in salary you could have been earning.</strong> Not doing this session doesn't save you ${{
          pricing
        }}
        — it costs you the compounding difference. The ${{ pricing }} is not the decision. The decision is what your
        next 12 months cost you by default.
      </div>

      <!-- Inline CTA -->
      <div
        class="inline-cta"
        style="margin-top: 28px"
      >
        <div class="inline-cta-text">
          <strong>${{ pricing }} now. $25,400 median return.</strong> The math doesn't require persuasion.
          <div class="inline-proof">100% money-back guarantee · No subscription · No follow-up charges</div>
        </div>
        <a
          class="btn-inline"
          href="#book"
          @click.prevent="$emit('book')"
          >Book — ${{ pricing }}</a
        >
      </div>
      <div
        v-if="!paymentProcessing"
        class="skip"
        @click="$emit('skip')"
      >
        No thanks - I'm fine figuring it out the hard way
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
defineEmits(["book", "skip"])

// PROPS
defineProps({
  pricing: {
    type: String,
    default: "49"
  },
  paymentProcessing: {
    type: Boolean,
    default: false
  }
})
</script>

<style lang="scss" scoped>
.i-roi-section {
  background: var(--purple-ultra);

  .roi-visual {
    display: flex;
    align-items: center;
    gap: 20px;
    margin: 36px 0;
    flex-wrap: wrap;
  }

  .roi-box {
    flex: 1;
    min-width: 130px;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 24px;
    text-align: center;

    &__label {
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.06em;
      text-transform: uppercase;
      margin-bottom: 8px;
    }

    &__amount {
      font-size: 2.4rem;
      font-weight: 800;
      line-height: 1;
      margin-bottom: 6px;
      letter-spacing: -0.02em;

      &--small {
        color: var(--text-body);
      }

      &--large {
        color: var(--green-dark);
      }
    }

    &__sub {
      font-size: 0.8rem;
      color: var(--muted);
    }
  }

  .roi-arrow {
    font-size: 1.8rem;
    color: var(--border);
    font-weight: 300;
    flex-shrink: 0;
  }

  .roi-multiplier {
    background: var(--grad-primary);
    color: #fff;
    border-radius: var(--radius);
    padding: 24px;
    text-align: center;
    flex: 1;
    min-width: 130px;
    box-shadow: 0 8px 32px #6b5cff4d;

    &__label {
      font-size: 0.75rem;
      font-weight: 700;
      color: #ffffffb3;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      margin-bottom: 8px;
    }

    &__amount {
      font-size: 2.4rem;
      font-weight: 800;
      line-height: 1;
      margin-bottom: 6px;
      letter-spacing: -0.02em;
      color: #fff;
    }

    &__sub {
      font-size: 0.8rem;
      color: #ffffffb3;
    }
  }

  .roi-callout {
    background: var(--navy-mid);
    color: #f7f4ef;
    border-radius: var(--radius);
    padding: 24px 28px;
    font-size: 0.95rem;
    line-height: 1.65;
    margin-top: 12px;

    strong {
      color: var(--purple-lt);
    }
  }
}
</style>
